import { useCallback } from 'react';

import { GA4_EVENT_ADD_TO_CART } from '@/data/constants/gtm';
import { getGTMConfig } from '@/data/events/handlers/gtm';
import { isB2BStore, useSettings } from '@/data/Settings';
import { logEventWithValues } from '@/data/utils/logEventsUtils';
import { useMobileApp } from '@/utils/useMobileApp';
import { B2B, B2C } from 'presentation/events/constants/eventConstants';
import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';
import { GA4_BOT_CHANNEL_MOBILE } from 'presentation/events/hooks/useTrackDibot';
import { triggerGA4EcommerceEvent } from 'presentation/events/services/ga4EcommerceService';
import { GA4ChatbotEventParams } from 'presentation/events/types/ga4Types';
import { buildGA4EcommerceEventPayload } from 'presentation/events/utils/ga4Utils';
import { SendAddToCartEventProps } from './types';

export const useTrackAddToCartGA4 = () => {
	const { settings } = useSettings();
	const { physicalStoreId } = useCurrentPhysicalStoreId();
	const { isMobileApp } = useMobileApp();
	const isB2B = isB2BStore(settings);

	return useCallback(
		(props: SendAddToCartEventProps) => {
			const { ga4 } = getGTMConfig(settings);

			if (!ga4 || !physicalStoreId) return;

			const payload = buildGA4EcommerceEventPayload({
				event: GA4_EVENT_ADD_TO_CART,
				settings,
				physicalStoreId,
				...props,
				deliveryDays: props.deliveryDateEventParams,
			});

			if (!isMobileApp) {
				triggerGA4EcommerceEvent(payload, {
					cleanup: true,
					eventParams: { ...props.chatbotEventParams },
				});
				return;
			}

			const chatbotEventParams: GA4ChatbotEventParams =
				props.chatbotEventParams.chatbot_event === 'true'
					? { ...props.chatbotEventParams, channel: GA4_BOT_CHANNEL_MOBILE }
					: props.chatbotEventParams;

			const mobilePayload = {
				...payload.ecommerce,
				app_thd: !isB2B ? B2C : B2B,
				...chatbotEventParams,
			};
			logEventWithValues(payload.event, mobilePayload);
		},
		[isB2B, isMobileApp, physicalStoreId, settings]
	);
};
